import { ColDef } from 'ag-grid-community';
import { AwsRegion, DEFAULT_AWS_REGION, ItemMetadata } from '../common/api.types';
import { UserType } from './accessManagement.types';

/**
 * Reports Configuration Type Definitions
 *
 * These types define the structure of reports configuration data
 * used throughout the Verity Reports Configuration feature.
 *
 * ## Null vs Undefined Convention
 *
 * This file uses `| null` consistently for nullable fields to align with GraphQL conventions:
 * - GraphQL APIs return `null` (not `undefined`) for missing/empty values
 * - API response types and client-side types use `| null` for consistency
 * - Optional parameters (using `?`) indicate the field can be omitted entirely
 *
 * Avoid mixing `?` (optional) with `| null` as this creates ambiguity between
 * "not provided" (undefined) and "explicitly empty" (null).
 */

/**
 * Report location type options
 */
export type ReportLocationType = 'S3';

/**
 * Report visibility level - determines which user types can see the report.
 *
 * Uses UserType enum to control access:
 * - TECH_ADMIN: Only developers/tech admins can see (for reports in development)
 * - VERITY_ADMIN: Verity admins and above can see (for reports in review/testing)
 * - READ_ONLY: All users can see (fully released reports)
 *
 * This is particularly useful for:
 * - Hiding incomplete reports from end users
 * - Gradual rollout of new reports
 * - Testing reports in production environment with limited visibility
 */
export type ReportVisibilityLevel = UserType.TECH_ADMIN | UserType.VERITY_ADMIN | UserType.READ_ONLY;

/**
 * Raw API response for reports configuration.
 *
 * Both `report_col_definition` and `report_location_details` are returned as JSON strings
 * from the backend because AppSync cannot define dynamic schemas for these fields.
 * These strings are parsed into typed objects on the client side.
 */
export interface ReportsConfigurationAPIResponse {
  report_id: number;
  /**
   * Unique code identifier for the report.
   *
   * This is a developer-defined unique identifier that remains consistent across
   * all environments (dev, staging, prod), unlike report_id which is auto-generated
   * by the database and may vary between environments.
   *
   * Format requirements:
   * - Uppercase letters (A-Z) and underscores (_) only
   * - No spaces, numbers, or special characters
   * - Must be unique across all reports
   *
   * @example 'MONTHLY_REVENUE_REPORT', 'PRODUCT_PERFORMANCE_SUMMARY'
   */
  report_unique_code: string;
  report_name: string;
  report_description: string;
  report_location_type: ReportLocationType;
  /** JSON string from API - parsed to ReportLocationDetails on client side */
  report_location_details: string | null;
  /** JSON string from API - parsed to ReportColDefinition[] on client side */
  report_col_definition: string;
  /**
   * Visibility level controlling which user types can access this report.
   *
   * - TECH_ADMIN: Only tech admins/developers can see (reports under development)
   * - VERITY_ADMIN: Verity admins and tech admins can see (reports in review)
   * - READ_ONLY: All users can see (fully released reports)
   */
  report_visibility_level: ReportVisibilityLevel;
  /**
   * Group name for organizing related reports together.
   * Used to display reports in logical groupings in the UI.
   * If null/empty, defaults to "UNGROUPED".
   */
  report_group_name: string | null;
  item_metadata: ItemMetadata;
}

/**
 * Client-side representation of reports configuration.
 *
 * Extends the API response but with parsed fields:
 * - `report_col_definition`: JSON string → ReportColDefinition[]
 * - `report_location_details`: JSON string → ReportLocationDetails | null
 */
export interface ReportsConfiguration extends Omit<
  ReportsConfigurationAPIResponse,
  'report_col_definition' | 'report_location_details'
> {
  report_col_definition: ReportColDefinition[];
  report_location_details: ReportLocationDetails | null;
}

/**
 * Structure defining a single column in a report table.
 *
 * This extends AG-Grid's ColDef type to allow developers to use any AG-Grid
 * column property directly, while ensuring required fields are present.
 *
 * ## Required Properties
 * - `field`: The key name in the data source (must match API response field names)
 * - `headerName`: Human-readable column header displayed in the table
 *
 * ## Commonly Used Optional Properties
 * - `hide`: Whether the column is hidden by default (default: false)
 * - `filter`: Enable filtering (boolean) or specify filter type ('agTextColumnFilter', etc.)
 * - `cellDataType`: Data type for the column ('text', 'number', 'boolean', 'date', 'dateString', 'object')
 * - `width`: Fixed column width in pixels
 * - `minWidth`: Minimum column width
 * - `maxWidth`: Maximum column width
 * - `flex`: Flex sizing (column takes up proportion of available space)
 * - `sortable`: Enable sorting (default: true)
 * - `resizable`: Enable column resizing (default: true)
 * - `pinned`: Pin column to 'left' or 'right'
 * - `cellRenderer`: Custom cell renderer component
 * - `valueFormatter`: Function to format cell value for display
 *
 * @see https://www.ag-grid.com/react-data-grid/column-definitions/
 *
 * @example
 * ```json
 * [
 *   { "field": "user_id", "headerName": "User ID", "filter": true },
 *   { "field": "amount", "headerName": "Amount", "cellDataType": "number", "filter": "agNumberColumnFilter" },
 *   { "field": "created_date", "headerName": "Created", "cellDataType": "date", "hide": true }
 * ]
 * ```
 */
export interface ReportColDefinition extends ColDef {
  /**
   * The field name from the data source. Required.
   * Must match the exact key name from the API/data response.
   */
  field: string;
  /**
   * The column header text displayed in the table. Required.
   */
  headerName: string;
}

/**
 * Location details for S3-based report data sources.
 */
export interface ReportLocationDetails {
  /** S3 URL pointing to the report data location */
  report_location_url: string | null;
  /** AWS region for the S3 bucket (optional, defaults to us-east-1) */
  report_location_region?: AwsRegion;
}

// ============================================================================
// Form-related Types
// ============================================================================

/**
 * Form mode type - create or edit
 */
export type ReportFormMode = 'create' | 'edit';

/**
 * Form values for creating or editing a report configuration.
 * Used by the report configuration form component.
 */
export interface ReportConfigurationFormValues {
  /**
   * Unique code identifier for the report (uppercase letters and underscores only).
   * @example 'MONTHLY_REVENUE_REPORT'
   */
  report_unique_code: string;
  report_name: string;
  report_description: string;
  report_location_type: ReportLocationType;
  report_location_url: string;
  /** AWS region for S3 location (only applicable when report_location_type is 'S3') */
  report_location_region: AwsRegion;
  /** Array of column definitions for the report (AG-Grid ColDef format) */
  report_col_definition: ReportColDefinition[];
  /** Visibility level controlling which user types can access this report */
  report_visibility_level: ReportVisibilityLevel;
  /** Group name for organizing related reports (defaults to "UNGROUPED" if null/empty) */
  report_group_name: string | null;
  /** Preserves metadata (created_at, created_by) during edit operations; null for create */
  item_metadata: ItemMetadata | null;
}

/**
 * Default form values for report_location_region
 */
export const DEFAULT_REPORT_LOCATION_REGION: AwsRegion = DEFAULT_AWS_REGION;

/**
 * Mutation payload for creating or updating report configuration via GraphQL API.
 * This interface is used only at the API boundary in the service layer.
 * All dynamic fields are serialized to JSON strings for the API.
 *
 * @property report_id - Null for create operations, report ID for update operations
 * @property report_unique_code - Developer-defined unique identifier (uppercase letters and underscores only)
 * @property report_col_definition - JSON string format required by the API
 * @property report_location_details - JSON string format required by the API
 * @property report_visibility_level - Controls which user types can access this report
 * @property item_metadata - Preserves audit fields during updates; null for new records
 */
export interface ReportConfigurationMutationPayload {
  report_id: number | null;
  report_unique_code: string;
  report_name: string;
  report_description: string;
  report_location_type: ReportLocationType;
  /** JSON string format - conversion handled in service layer */
  report_location_details: string | null;
  /** JSON string format - conversion handled in service layer */
  report_col_definition: string;
  report_visibility_level: ReportVisibilityLevel;
  report_group_name: string | null;
  item_metadata: ItemMetadata | null;
}

/**
 * Options for report location type selection in form components.
 */
export const REPORT_LOCATION_TYPE_OPTIONS: { value: ReportLocationType; label: string }[] = [
  { value: 'S3', label: 'S3 Location' },
];

/**
 * Options for report visibility level selection in form components.
 * Controls which user types can see the report.
 */
export const REPORT_VISIBILITY_LEVEL_OPTIONS: { value: ReportVisibilityLevel; label: string; description: string }[] = [
  {
    value: UserType.TECH_ADMIN,
    label: 'Tech Admin Only',
    description: 'Only developers/tech admins can see this report (for development phase)',
  },
  {
    value: UserType.VERITY_ADMIN,
    label: 'Verity Admin',
    description: 'Verity admins and tech admins can see this report (for review/testing phase)',
  },
  {
    value: UserType.READ_ONLY,
    label: 'All Users',
    description: 'All users can see this report (fully released)',
  },
];

/**
 * Regex pattern for validating report_unique_code.
 * Only uppercase letters (A-Z) and underscores (_) are allowed.
 * No spaces, numbers, or special characters.
 *
 * @example Valid: 'MONTHLY_REVENUE', 'PRODUCT_REPORT', 'ANNUAL_SUMMARY'
 * @example Invalid: 'monthly_revenue', 'REPORT 1', 'REPORT-123'
 */
export const REPORT_UNIQUE_CODE_PATTERN = /^[A-Z_]+$/;

/**
 * Validation helper for report_unique_code field.
 * Returns true if the code is valid, false otherwise.
 */
export const isValidReportUniqueCode = (code: string): boolean => {
  return REPORT_UNIQUE_CODE_PATTERN.test(code) && code.length > 0;
};

// ============================================================================
// Report Metadata Types (from S3 metadata.json)
// ============================================================================

/**
 * Metadata information about a report stored in S3.
 *
 * This metadata is stored in `metadata.json` within the report's S3 folder
 * and provides information about when the report data was last refreshed.
 *
 * This interface is extensible - additional fields can be added as needed
 * (e.g., record count, data source info, etc.)
 */
export interface ReportS3Metadata {
  /**
   * ISO 8601 timestamp indicating when the report data was last updated.
   * @example "2026-01-29T20:04:14.931Z"
   */
  timestamp: string;
  /**
   * Identifier of the process/job that last updated the report data.
   * @example "verity_asin_fpn_mapping_report-alpha"
   */
  last_updated_by: string;
  /**
   * Optional: Name of the AWS Glue job that generates this report data.
   * Can be used to link to Glue job monitoring/logs.
   * @example "verity-asin-fpn-mapping-job"
   */
  glue_job_name?: string;
  /**
   * Optional: AWS region where the Glue job runs.
   * @example "us-west-2"
   */
  glue_job_region?: string;
}

// ============================================================================
// Report Data Types
// ============================================================================

/**
 * Client-generated row identifier field name.
 *
 * ## Why Client-Side Row ID is Necessary
 *
 * Report data fetched from APIs or S3 may not have unique identifiers for each row.
 * The source data could be:
 * - CSV exports without primary keys
 * - Aggregated data without natural unique identifiers
 * - Third-party data sources with duplicate or missing IDs
 *
 * AG-Grid requires unique row identifiers for:
 * - Efficient row selection and state management
 * - Virtual scrolling performance with large datasets (100K+ rows)
 * - Row-level operations (selection, highlighting, updates)
 * - Stable row identification during sorting/filtering
 *
 * ## Implementation Choice: Numeric Index
 *
 * We use a simple numeric index (0, 1, 2, ...) instead of UUIDs because:
 * 1. **Performance**: Zero overhead - uses array index directly
 * 2. **AG-Grid Optimization**: AG-Grid performs best with numeric row IDs
 * 3. **Memory Efficiency**: Numbers use less memory than UUID strings (36 chars each)
 * 4. **No Dependencies**: No external library needed (vs nanoid, uuid)
 * 5. **Speed**: For 100K records, index assignment is O(n) with minimal overhead
 *
 * The underscore prefix (_row_id) indicates this is a client-generated field,
 * not part of the original API response data.
 */
export const CLIENT_ROW_ID_FIELD = '_row_id';

/**
 * Type for a single report data record with client-generated row identifier.
 *
 * The _row_id field is added client-side to ensure each row has a unique identifier
 * for AG-Grid operations, regardless of whether the source data has natural keys.
 */
export interface ReportDataRecord extends Record<string, unknown> {
  /**
   * Client-generated unique row identifier (numeric index).
   * Added during data fetch to ensure every row has a stable, unique ID.
   * Used by AG-Grid for row identification, selection, and virtual scrolling.
   */
  _row_id: number;
}

/**
 * Parameters required for fetching report data.
 */
export interface GetReportDataParams {
  reportUniqueCode: string;
  reportLocationType: ReportLocationType;
  /** Location details - null when report uses API location type */
  reportLocationDetails: ReportLocationDetails | null;
  /** Optional AbortSignal for cancelling in-flight S3 requests */
  signal?: AbortSignal;
}

/**
 * Warning information for partial data fetch failures.
 * Included when some files fail to load but graceful degradation is enabled.
 */
export interface ReportDataWarning {
  /** The S3 key or file path that failed to load */
  source: string;
  /** Human-readable error message */
  message: string;
}

/**
 * Processed response containing report data for UI consumption.
 *
 * Each record in the data array includes a client-generated _row_id field
 * to ensure unique identification for AG-Grid operations.
 */
export interface GetReportDataResponse {
  reportUniqueCode: string;
  /** Array of data records, each with a client-generated _row_id for AG-Grid */
  data: ReportDataRecord[];
  /**
   * Metadata from S3 metadata.json file (only present for folder-based S3 reports).
   * Contains information like last update timestamp and data source identifier.
   */
  metadata?: ReportS3Metadata;
  /**
   * Warnings about partial failures when loading report data.
   * Present when some files failed to load but graceful degradation was enabled.
   * The UI should display these warnings to inform users about incomplete data.
   */
  warnings?: ReportDataWarning[];
}

// ============================================================================
// S3 Fetch Related Types
// ============================================================================

/**
 * Result of metadata fetch operation.
 * Used internally by the service layer when comparing S3 metadata with cached values.
 */
export interface MetadataFetchResult {
  /** The fetched metadata, or undefined if not found */
  metadata: ReportS3Metadata | undefined;
  /** Whether metadata changed from cached version */
  metadataChanged: boolean;
}

/**
 * Parameters for S3 report data fetching.
 * Uses localStorage for metadata caching and browser/CloudFront cache for data files.
 */
export interface GetReportDataFromS3Params {
  /** The unique code identifying the report */
  reportUniqueCode: string;
  /** S3 URI pointing to the report data (file or folder) */
  s3URI: string | null;
  /** Optional AbortSignal for cancelling in-flight S3 requests */
  signal?: AbortSignal;
}
